import React, { useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';

import Button from 'components/CustomButtons/Button';
import { BodyMBold, BodySBold, DescriptionS } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

import { Close } from '@material-ui/icons';

import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MobileStepper from '@material-ui/core/MobileStepper';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/paymentMethodsStyle';

import { getPaymentMethodLabels, getCommonLabels } from 'redux/modules/dynamicLabels/selectors';

import BOMServices from 'services/BOM/BOM';
import { getLanguage } from 'utils/language/language';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { upIdSelector } from 'redux/modules/session/selectors';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

interface BOMIntroProps {
	open: boolean;
	onClose: () => void;
}

// @ts-ignore
const useStyles = makeStyles(styles);

interface Intro {
	title: string;
	type: 'image' | 'video';
	description: string;
	url: string;
}

const Intro = ({ open, onClose }: BOMIntroProps) => {
	const theme = useTheme();
	const classes = useStyles();

	const language = getLanguage();
	const commonLabels = useSelector(getCommonLabels);
	const paymentMethodLabels = useSelector(getPaymentMethodLabels);
	const accId = useSelector(upIdSelector);

	const [intro, setIntro] = useState<Intro[]>([]);
	const [activeStep, setStep] = useState<number>(0);

	const isLastSlide = useMemo(() => activeStep === intro.length - 1, [activeStep, intro]);

	const onStepClick = (step: number) => {
		return () => setStep((state) => state + step);
	};

	const onChangeIndexCallback = (step: number) => {
		setStep(step);
	};

	// state reset on open/close modal
	useEffect(() => setStep(0), [open]);

	useEffect(() => {
		const subscriber = BOMServices.getIntro<Intro[]>(language).subscribe(({ response }) => {
			setIntro(response ?? []);
		});

		return () => {
			subscriber.unsubscribe();
		};
	}, [language]);

	useEffect(() => {
		if (activeStep) {
			setDataLayer('tutorial_view', getLanguage(), null, accId, null, {
				infoType: 'Bank Muscat Tutorial',
				tutorialStep: activeStep,
			});
		}
	}, [activeStep]);

	return (
		<Dialog open={open}>
			<MuiDialogTitle disableTypography>
				<BodyMBold customClassName={classes.marginBottomNone}>
					{paymentMethodLabels?.paymentsMethodBankMuscat}
				</BodyMBold>
				<IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
					<Close />
				</IconButton>
			</MuiDialogTitle>
			<DialogContent dividers>
				<BodySBold>{paymentMethodLabels?.overview}</BodySBold>
				<DescriptionS>{paymentMethodLabels?.bomIntro?.overview}</DescriptionS>
				<div className={classes.DarkHeading}>
					<BodyMBold customClassName={classes.marginBottomNone}>
						{paymentMethodLabels?.bomIntro?.howToSetup}
					</BodyMBold>
				</div>
				<div className={classes.introSlideWrapper}>
					<AutoPlaySwipeableViews
						axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
						index={activeStep}
						autoPlay={false}
						autoplay={false}
						onChangeIndex={onChangeIndexCallback}
						enableMouseEvents
					>
						{intro.map((step, index) => (
							<div key={`intro_slide_${index}`} className={classes.introSlide}>
								<div className={classes.introMedia}>
									{step.type === 'image' ? <img src={step.url} /> : <video controls src={step.url} />}
								</div>
								<BodySBold dir={theme.direction}>{step.title}</BodySBold>
								<DescriptionS dir={theme.direction}>{step.description}</DescriptionS>
							</div>
						))}
					</AutoPlaySwipeableViews>
					{intro.length > 1 && (
						<MobileStepper
							variant="dots"
							steps={intro.length}
							position="static"
							className={classes.introPagination}
							activeStep={activeStep}
							nextButton={null}
							backButton={null}
						/>
					)}
				</div>
			</DialogContent>
			<DialogActions className={classes.dialogFooter}>
				<Button color="secondary-light" onClick={onStepClick(-1)} disabled={activeStep === 0}>
					{commonLabels?.back}
				</Button>
				<ConditionalRender
					show={!isLastSlide}
					onTrue={() => (
						<Button color="primary-light" onClick={onStepClick(1)}>
							{commonLabels?.next}
						</Button>
					)}
					onFalse={() => (
						<Button color="primary-light" onClick={onClose}>
							{paymentMethodLabels?.bomIntro?.close}
						</Button>
					)}
				/>
			</DialogActions>
		</Dialog>
	);
};

export default Intro;
